import { isPaymentRoute } from '~/utils/optimizely/payment-routes'
import { PAYMENT_STEP } from '~/constants/application/health-insurance-v2'
import { adoptCrossOriginVisitorId } from '~/utils/optimizely/byoid'

declare global {
  interface Window {
    optimizely?: any
    dataLayer?: any[]
  }
}

interface OptimizelyData {
  optimizely_visitor_id: any
  optimizely_active_experiment_ids: string[]
  optimizely_variation_map: {
    [key: string]: any
  }
}

// Use the tracking plugin to push Optimizely data to the dataLayer
function pushOptimizelyToDataLayer (optimizelyData: OptimizelyData) {
  if (
    typeof window !== 'undefined' &&
    Array.isArray(optimizelyData.optimizely_active_experiment_ids) &&
    optimizelyData.optimizely_active_experiment_ids.length > 0
  ) {
    const nuxtApp = useNuxtApp && useNuxtApp()
    const tracking = nuxtApp && (nuxtApp.$tracking || nuxtApp.tracking)
    if (tracking && typeof tracking.customEvent === 'function') {
      tracking.customEvent('optimizely_decision', { ...optimizelyData })
    } else if (window.dataLayer) {
      // fallback for SSR or if tracking plugin is not available
      window.dataLayer.push({
        event: 'optimizely_decision',
        ...optimizelyData
      })
    }
  }
}

export default defineNuxtPlugin({
  name: 'optimizely',
  parallel: true,
  setup () {
    class Optimizely {
      optimizelyLoaded: boolean = false

      init (): void {
        if (!import.meta.client) return

        const route = useRoute()
        if (isPaymentRoute(route.path)) {
          this.destroy()
          return
        }

        if (!this.optimizelyLoaded) {
          // Load Optimizely script
          this.addOptimizelyScript(route.path)
        } else {
          this.setOptimizelyDetails()
        }
      }

      destroy (): void {
        if (!import.meta.client) return

        const scriptEl = document.getElementById('optimizely-script')
        if (scriptEl) {
          scriptEl.remove()
        }

        delete window.optimizely
        this.optimizelyLoaded = false
      }

      private addOptimizelyScript (routePath: string): void {
        // Optimizely uses eval() at runtime; never load it on payment routes
        // to eliminate 'unsafe-eval' from those pages' CSP.
        const PAYMENT_ROUTE_PATTERNS = [
          '/health-insurance/application/payment',
          `/health-insurance/applicationV2/${PAYMENT_STEP}`
        ]
        if (
          PAYMENT_ROUTE_PATTERNS.some(pattern =>
            window.location.pathname.startsWith(pattern)
          )
        ) {
          return
        }

        const { optimizelyScript } = useRuntimeConfig().public
        const scriptId = 'optimizely-script'

        if (document.getElementById(scriptId)) {
          this.optimizelyLoaded = true
          this.setOptimizelyDetails(false)
          return
        }

        // Cross-origin identity with HIC (healthinsurancecomparison.com.au)
        // uses BYOID: HIC appends its Optimizely end-user id as ?oeu= on the
        // hand-off URL, and we adopt it into our optimizelyEndUserId cookie
        // BEFORE the snippet is appended below, so Optimizely reads it on init
        // and buckets the visitor identically to HIC. Scoped to quotes-results
        // — the only page users land on from HIC. This replaced the old
        // waitForOriginSync iframe sync, which browsers with partitioned
        // storage (Safari ITP) block. See app/utils/optimizely/byoid.ts and
        // docs/optimizely-ab-tests.md § Cross-Origin Tracking.
        if (routePath.startsWith('/health-insurance/quotes-results')) {
          adoptCrossOriginVisitorId()
        }

        const script = document.createElement('script')
        script.id = scriptId
        script.src = `${optimizelyScript}`
        script.type = 'text/javascript'
        script.async = true
        script.defer = true
        script.onload = () => {
          this.optimizelyLoaded = true
          this.setOptimizelyDetails(false)
        }
        document.head.appendChild(script)
      }

      private setOptimizelyDetails (
        triggerOptimizelyActivation: boolean = true
      ): void {
        // window.optimizely can be a plain command-queue array before the
        // snippet loads (truthy but not usable) — require the real API before
        // reading decisions.
        if (!window.optimizely || typeof window.optimizely.get !== 'function')
          return

        try {
          if (triggerOptimizelyActivation) {
            // Force Optimizely to re-evaluate experiments for current page
            window.optimizely?.push({ type: 'activate' })
          }

          const optimizelyData = {
            optimizely_visitor_id: window.optimizely?.get('visitor_id'),
            optimizely_active_experiment_ids:
              window.optimizely?.get('state').getActiveExperimentIds() || [],
            optimizely_variation_map:
              window.optimizely?.get('state').getVariationMap() || {}
          }
          useUtmsStore().optimizely_utms = optimizelyData
          useUtmsStore().GENERATE_OPTIMIZELY_DATA()

          pushOptimizelyToDataLayer(optimizelyData)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.debug('Optimizely evaluation failed:', error)
        }
      }
    }

    const optimizelyClient = new Optimizely()

    return {
      provide: {
        optimizelyClient
      }
    }
  }
})
